import moment from "moment";
import "moment/locale/pt-br";
import styled from "styled-components";
import { device, size } from "../../../constants";
import { useTheme } from "../../../contexts/ThemeContext";
import { useWindowSize } from "../../../hooks/useWindowResize";
import { calculateTimerPost } from "../../../utils/blog/calculateTimerPost";
import { BlogButton } from "../buttons/button";
import { useTranslation } from "next-i18next";
import { normalizePostContent } from "../../../utils/blog/normalizePostContent";

moment.locale("pt-br");

const Card = styled.div``;

export const CardTitle = styled.h2`
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-top: 16px;
  position: relative;
  text-align: left;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 4px;
    background: ${({ theme }) => theme.colors["branding-04"]};
    border-radius: 100px;
  }

  @media ${device.laptop} {
    font-size: 32px;
    line-height: 40px;

    margin-bottom: 8px;
    padding-top: 24px;

    &::before {
      left: 0;
      transform: none;
      width: 48px;
    }
  }

  :is([data-theme="dark"] &){
    color: ${({ theme }) => theme.colors["neutral-01"]};

    &::before {
      background: ${({ theme }) => theme.colors["vivid-04"]};
    }
  }
`;

export const CardPubDate = styled.small`
  color: ${({ theme }) => theme.colors["neutral-04"]};
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  text-align: left;
  margin-bottom: 16px;

  @media ${device.laptop} {
    text-align: left;
    font-size: 16px;
    line-height: 150%;
    margin-bottom: 24px;
  }

  :is([data-theme="dark"] &){
    color: ${({ theme }) => theme.colors["neutral-03"]};
  }
`;

export const CardImage = styled.img`
  border-radius: 8px;
  object-fit: cover;
  width: 100%;
  margin: 0 auto;
  margin-bottom: 16px;

  @media ${device.laptop} {
    width: 100%;
    height: 100%;
    object-position: center;
    margin: unset;
  }

  @media ${device.laptopL} {
    width: 764px;
    height: 616px;
  }
`;

export const CardText = styled.div`
  overflow: hidden;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: ${({ hasPostAuthor }) => (hasPostAuthor ? 7 : 8)};
  -webkit-box-orient: vertical;

  text-align: justify;
  
  color: ${({ theme }) => theme.colors["neutral-05"]} !important;
  
  * {
    color: ${({ theme }) => theme.colors["neutral-05"]} !important;
  }

  
  :is([data-theme="dark"] &){
    color: ${({ theme }) => theme.colors["neutral-01"]} !important;
    
    * {
      color: ${({ theme }) => theme.colors["neutral-01"]} !important;
    }
  }
  
  & strong {
    font-weight: 600;
  }

  & p {
    font-size: 18px;
    font-weight: 400;
    text-align: justify;
    line-height: 32px;
  }

  & :is(h1, h2, h3, h4, h5, h6) {
    font-weight: 700;
    font-size: 20px;
    line-height: 32px;
    margin: 0;
  }

  @media ${device.laptop} {
    & p {
      font-size: 20px;
      line-height: 40px;
    }

    & :is(h1, h2, h3, h4, h5, h6) {
      font-size: 22px;
    }
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;

  .content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  @media ${device.laptop} {
    display: grid;
    grid-template-columns: 3fr 2fr;
    gap: 32px;

    .content {
      width: 100%;
    }
  }
`;

const CTA = styled(BlogButton)`
  width: 100%;
  margin: 0 auto;
  margin-top: 24px;
  height: 48px;
  line-height: 40px;
  letter-spacing: 10%;
  max-width: 342px;
  border-width: 1.5px;

  @media ${device.laptop} {
    max-width: 372px;
    font-size: 18px;
    font-weight: 600;
    height: 56px;
    margin-inline: unset;
  }
`;

const PostAuthor = styled.p`
  margin-top: -8px;
  margin-bottom: 16px;
  font-size: 18px;
  line-height: 26px;
  font-weight: 500;
  letter-spacing: 0%;
  color: ${({ theme }) => theme.colors["neutral-05"]};
  
  :is([data-theme='dark'] &) {
    color: ${({ theme }) => theme.colors["neutral-02"]};
  }
  
  @media ${device.laptop} {
    margin-bottom: 24px;
    line-height: 40px;
    font-size: 24px;
  }
`;

export function RecentPostCard({ data }) {
	const { width } = useWindowSize();
	const { theme } = useTheme();
	const { t, i18n } = useTranslation();
	const isDark = theme === "dark";

	const isLaptop = width >= size.laptop;

	const hasPostAuthor = !!data?.metadata?.autor_do_post;

	const slicedPost = normalizePostContent(
		data?.metadata.texto_preview_inicio || data?.metadata.texto_inicio,
	);

	return (
		<Card>
			<CardBody>
				{isLaptop && <CardImage src={data.metadata.imagem_inicio.url} />}

				<div className="content">
					<CardTitle>{data.title}</CardTitle>

					{hasPostAuthor && (
						<PostAuthor>{data.metadata.autor_do_post}</PostAuthor>
					)}

					<CardPubDate>
						{t("blog.published")}{" "}
						{calculateTimerPost(data?.created_at, i18n.language)}
					</CardPubDate>
					{!isLaptop && <CardImage src={data.metadata.imagem_inicio.url} />}
					<CardText hasPostAuthor={hasPostAuthor}>
						<div
							dangerouslySetInnerHTML={{
								__html: slicedPost,
							}}
						/>
					</CardText>
					<CTA variant="purple" outline={isDark} href={data.slug}>
						{t("blog.showContent")}
					</CTA>
				</div>
			</CardBody>
		</Card>
	);
}
